import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import apiClient from '../../services/apiClient';
import { isTerminalActionStatus, resolveActionDisplayStatus } from '../../utils/actionStatusConfig';
import { summarizeActionProgress } from '../../utils/actionProgress';
import {
  Action,
  ActionDetail,
  ActionNotificationDto,
  ActionTransferProgress,
  RtuActionStateUpdateDto,
} from '../../types/actionTypes';
import { AppThunk, RootState } from '../store/store';

export interface ActionTracker {
  actionId: number;
  expanded: boolean;
  createdAtUtc: string;
  lastUpdatedAtUtc?: string;
}

export interface ActionNotificationState extends ActionNotificationDto {
  localId: string;
  read: boolean;
  receivedAtUtc: string;
}

interface ActionsState {
  actions: Action[];
  trackers: ActionTracker[];
  runningRtuStates: Record<string, RtuActionStateUpdateDto>;
  actionNotifications: ActionNotificationState[];
  /** Set once, right after a successful launch, so the activity center can
   * select/expand/reopen for that action; consumed (cleared) immediately
   * after the panel reacts to it. Never set by hydration/reconnect/WS paths. */
  justLaunchedActionId: number | null;
}

const initialState: ActionsState = {
  actions: [],
  trackers: [],
  runningRtuStates: {},
  actionNotifications: [],
  justLaunchedActionId: null,
};

const nowUtc = (): string => new Date().toISOString();

const getRunningStateKey = (state: RtuActionStateUpdateDto): string => {
  if (state.rtuId !== undefined && state.rtuId !== null) {
    return `${state.actionId}:${state.rtuId}`;
  }
  return `${state.actionId}:${state.ip}`;
};

const isTerminalTargetStatus = (status: unknown): boolean => {
  const normalized = String(status ?? '').trim().toUpperCase();
  return [
    'COMPLETED',
    'SUCCEEDED',
    'FAILED',
    'CANCELLED',
    'CANCELED',
    'TIMED_OUT',
    'BLOCKED',
    'SKIPPED',
    'OUTCOME_UNCONFIRMED',
  ].includes(normalized);
};

const shouldIgnoreActionSnapshot = (existing: Action | undefined, incoming: Action): boolean => {
  if (!existing) return false;
  const existingRevision = existing.revision;
  const incomingRevision = incoming.revision;
  if (existingRevision !== undefined && incomingRevision !== undefined && incomingRevision < existingRevision) {
    return true;
  }
  const existingTerminal = isTerminalActionStatus(resolveActionDisplayStatus(existing));
  const incomingTerminal = isTerminalActionStatus(resolveActionDisplayStatus(incoming));
  return existingTerminal && !incomingTerminal
    && (incomingRevision === undefined || existingRevision === undefined || incomingRevision <= existingRevision);
};

const mergeActionDetails = (
  existingDetails: ActionDetail[] | undefined,
  incomingDetails: ActionDetail[] | undefined,
): ActionDetail[] => {
  if (!incomingDetails) return existingDetails ?? [];
  const existingById = new Map(
    (existingDetails ?? [])
      .filter((detail) => detail.id !== undefined)
      .map((detail) => [detail.id, detail]),
  );

  return incomingDetails.map((incoming) => {
    const existing = incoming.id !== undefined ? existingById.get(incoming.id) : undefined;
    if (!existing) return incoming;
    if (existing.revision !== undefined && incoming.revision !== undefined
        && incoming.revision < existing.revision) {
      return existing;
    }
    if (isTerminalTargetStatus(existing.status) && !isTerminalTargetStatus(incoming.status)
        && (incoming.revision === undefined || existing.revision === undefined
          || incoming.revision <= existing.revision)) {
      return existing;
    }

    return {
      ...existing,
      ...incoming,
      transfer: incoming.transfer ?? existing.transfer,
      timeline: incoming.timeline ?? existing.timeline,
      commandHints: incoming.commandHints ?? existing.commandHints,
    };
  });
};

const upsertActionInState = (state: ActionsState, incoming: Action): Action => {
  const existingIndex = state.actions.findIndex((item) => item.id === incoming.id);
  const existing = existingIndex === -1 ? undefined : state.actions[existingIndex];
  if (shouldIgnoreActionSnapshot(existing, incoming)) {
    return existing as Action;
  }
  const merged = {
    ...(existing ?? {}),
    ...incoming,
    actionDetails: mergeActionDetails(existing?.actionDetails, incoming.actionDetails),
    artifacts: incoming.artifacts ?? existing?.artifacts,
  } as Action;

  if (existingIndex === -1) {
    state.actions.push(merged);
    return merged;
  }

  state.actions[existingIndex] = merged;
  return merged;
};

const ensureTrackerForAction = (state: ActionsState, action: Action): void => {
  const trackerIndex = state.trackers.findIndex((tracker) => tracker.actionId === action.id);
  const terminal = isTerminalActionStatus(resolveActionDisplayStatus(action));

  if (trackerIndex === -1) {
    state.trackers.unshift({
      actionId: action.id,
      expanded: false,
      createdAtUtc: nowUtc(),
      lastUpdatedAtUtc: nowUtc(),
    });
    return;
  }

  state.trackers[trackerIndex].lastUpdatedAtUtc = nowUtc();
  if (terminal) {
    state.trackers[trackerIndex].expanded = false;
  }
};

const applyActionSnapshot = (state: ActionsState, incoming: Action, track = true): void => {
  const merged = upsertActionInState(state, incoming);
  if (track) {
    ensureTrackerForAction(state, merged);
  }
};

const toNotificationLocalId = (notification: ActionNotificationDto): string => {
  const idPart = notification.id !== undefined ? String(notification.id) : 'unknown';
  const actionPart = notification.actionId !== undefined ? String(notification.actionId) : 'none';
  return `${idPart}:${actionPart}:${Date.now()}`;
};

export const cancelAction = createAsyncThunk('actions/cancelAction', async (actionId: number) => {
  const response = await apiClient.post('/actions/cancel', null, {
    params: { actionId },
  });
  return response.data as Action;
});

export const actionsSlice = createSlice({
  name: 'actions',
  initialState,
  reducers: {
    addAction: (state, action: PayloadAction<Action>) => {
      applyActionSnapshot(state, action.payload);
    },
    updateAction: (state, action: PayloadAction<Action>) => {
      applyActionSnapshot(state, action.payload);
    },
    hydrateActionSnapshots: (state, action: PayloadAction<Action[]>) => {
      action.payload.forEach((item) => applyActionSnapshot(state, item, false));
    },
    hydrateActiveActionSnapshots: (state, action: PayloadAction<Action[]>) => {
      const activeIds = new Set(action.payload.map((item) => item.id));
      // A tracked action (one with a visible activity-center row / open
      // progress dialog) must never be purged here just because it raced
      // ahead of this reconciliation's /active snapshot - e.g. it went
      // terminal between the /active request and response, and its final
      // WebSocket push simply hasn't landed yet. This reconciliation only
      // fetches individual details for tracked non-terminal actions in that
      // same request wave (see hydrateActiveActions in WebSocketListener),
      // so at the moment this reducer runs, "not yet terminal locally and
      // not in /active" does not mean stale - it can equally mean "already
      // finished, update in flight". Previously this purged the tracker
      // (though the action snapshot itself was restored a moment later by
      // the follow-up hydrateActionSnapshots dispatch), which silently
      // closed the still-open progress dialog / dropped the activity-center
      // row a few seconds after the action actually completed. On the real
      // deployed backend this reconciliation fetches up to 200 individual
      // action details over real network latency, so the race window is
      // multiple seconds wide - wide enough to hit in practice.
      const trackedIds = new Set(state.trackers.map((tracker) => tracker.actionId));
      state.actions = state.actions.filter((item) =>
        isTerminalActionStatus(resolveActionDisplayStatus(item)) || activeIds.has(item.id) || trackedIds.has(item.id)
      );
      // ACT-UI-013/F: the activity center must retain an action until the
      // user dismisses it (via dismissActionTracker) or logs out. Only drop
      // a tracker here if its action is genuinely gone from state entirely.
      state.trackers = state.trackers.filter((tracker) => {
        if (activeIds.has(tracker.actionId)) return true;
        return state.actions.some((item) => item.id === tracker.actionId);
      });
      Object.keys(state.runningRtuStates).forEach((key) => {
        const actionId = Number(key.split(':')[0]);
        if (!activeIds.has(actionId)) delete state.runningRtuStates[key];
      });
      action.payload.forEach((item) => applyActionSnapshot(state, item, true));
    },
    updateActionDetail: (
      state,
      action: PayloadAction<{ actionId: number; ip: string; detail: Partial<ActionDetail> }>
    ) => {
      const { actionId, ip, detail } = action.payload;
      const actionIndex = state.actions.findIndex((item) => item.id === actionId);
      if (actionIndex === -1) {
        return;
      }

      const actionItem = state.actions[actionIndex];
      const detailIndex = actionItem.actionDetails.findIndex((entry) => entry.ip === ip);
      if (detailIndex === -1) {
        return;
      }

      if (isTerminalTargetStatus(actionItem.actionDetails[detailIndex].status)
          && !isTerminalTargetStatus(detail.status)) {
        return;
      }
      const nextDetails = [...actionItem.actionDetails];
      nextDetails[detailIndex] = {
        ...nextDetails[detailIndex],
        ...detail,
      };

      state.actions[actionIndex] = {
        ...actionItem,
        actionDetails: nextDetails,
      };
    },
    applyActionTransferProgress: (state, action: PayloadAction<ActionTransferProgress>) => {
      const progress = action.payload;
      const actionIndex = state.actions.findIndex((item) => item.id === progress.actionId);
      if (actionIndex === -1) return;
      const actionItem = state.actions[actionIndex];
      const detailIndex = actionItem.actionDetails.findIndex((detail) => detail.id === progress.targetRunId);
      if (detailIndex === -1) return;
      const current = actionItem.actionDetails[detailIndex].transfer;
      if (current && progress.sequence <= current.sequence) return;
      actionItem.actionDetails[detailIndex].transfer = progress;
      const tracker = state.trackers.find((item) => item.actionId === progress.actionId);
      if (tracker) tracker.lastUpdatedAtUtc = nowUtc();
    },
    setActions: (state, action: PayloadAction<Action[]>) => {
      state.actions = action.payload;
      action.payload.forEach((item) => ensureTrackerForAction(state, item));
    },
    upsertActionTracker: (state, action: PayloadAction<{ actionId: number; expanded?: boolean }>) => {
      const existing = state.trackers.find((tracker) => tracker.actionId === action.payload.actionId);
      if (existing) {
        existing.expanded = action.payload.expanded ?? existing.expanded;
        existing.lastUpdatedAtUtc = nowUtc();
        return;
      }

      state.trackers.unshift({
        actionId: action.payload.actionId,
        expanded: action.payload.expanded ?? false,
        createdAtUtc: nowUtc(),
        lastUpdatedAtUtc: nowUtc(),
      });
    },
    setActionTrackerExpanded: (state, action: PayloadAction<{ actionId: number; expanded: boolean }>) => {
      const existing = state.trackers.find((tracker) => tracker.actionId === action.payload.actionId);
      if (!existing) {
        return;
      }
      existing.expanded = action.payload.expanded;
      existing.lastUpdatedAtUtc = nowUtc();
    },
    dismissActionTracker: (state, action: PayloadAction<number>) => {
      state.trackers = state.trackers.filter((tracker) => tracker.actionId !== action.payload);
    },
    applyRtuActionStateUpdate: (state, action: PayloadAction<RtuActionStateUpdateDto>) => {
      const update = action.payload;
      const runningKey = getRunningStateKey(update);

      if (update.running) {
        state.runningRtuStates[runningKey] = update;
      } else {
        delete state.runningRtuStates[runningKey];
      }

      const actionIndex = state.actions.findIndex((item) => item.id === update.actionId);
      if (actionIndex === -1) {
        return;
      }

      // IP is an execution-time address snapshot, never a target identity: a
      // running-state update must be matched by rtuId only, and dropped
      // (never guessed by IP) when rtuId is unavailable.
      if (update.rtuId === undefined || update.rtuId === null) {
        return;
      }

      const actionItem = state.actions[actionIndex];
      const detailIndex = actionItem.actionDetails.findIndex((detail) => detail.rtuId === update.rtuId);

      if (detailIndex === -1) {
        return;
      }

      const nextDetails = [...actionItem.actionDetails];
      if (isTerminalTargetStatus(nextDetails[detailIndex].status)
          && !isTerminalTargetStatus(update.status)) {
        return;
      }
      nextDetails[detailIndex] = {
        ...nextDetails[detailIndex],
        ip: update.ip,
        status: update.status,
        phase: update.phase,
        completedAt: update.running ? nextDetails[detailIndex].completedAt : update.updatedAtUtc,
      };

      const nextAction = {
        ...actionItem,
        actionDetails: nextDetails,
      };
      state.actions[actionIndex] = nextAction;
      ensureTrackerForAction(state, nextAction);
    },
    setRunningRtuStates: (state, action: PayloadAction<RtuActionStateUpdateDto[]>) => {
      state.runningRtuStates = {};
      action.payload.forEach((item) => {
        if (item.running) {
          state.runningRtuStates[getRunningStateKey(item)] = item;
        }
      });
    },
    receiveActionNotification: (state, action: PayloadAction<ActionNotificationDto>) => {
      state.actionNotifications.unshift({
        ...action.payload,
        localId: toNotificationLocalId(action.payload),
        read: false,
        receivedAtUtc: nowUtc(),
      });
    },
    markActionNotificationRead: (state, action: PayloadAction<string>) => {
      const notification = state.actionNotifications.find((item) => item.localId === action.payload);
      if (notification) {
        notification.read = true;
      }
    },
    clearActionNotifications: (state) => {
      state.actionNotifications = [];
    },
    clearActionTrackingState: (state) => {
      state.actions = [];
      state.trackers = [];
      state.runningRtuStates = {};
      state.actionNotifications = [];
      state.justLaunchedActionId = null;
    },
    announceJustLaunchedAction: (state, action: PayloadAction<number>) => {
      state.justLaunchedActionId = action.payload;
    },
    acknowledgeJustLaunchedAction: (state) => {
      state.justLaunchedActionId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(cancelAction.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      applyActionSnapshot(state, action.payload);
    });
  },
});

export const {
  addAction,
  updateAction,
  hydrateActionSnapshots,
  hydrateActiveActionSnapshots,
  updateActionDetail,
  applyActionTransferProgress,
  setActions,
  upsertActionTracker,
  setActionTrackerExpanded,
  dismissActionTracker,
  applyRtuActionStateUpdate,
  setRunningRtuStates,
  receiveActionNotification,
  markActionNotificationRead,
  clearActionNotifications,
  clearActionTrackingState,
  announceJustLaunchedAction,
  acknowledgeJustLaunchedAction,
} = actionsSlice.actions;

export const selectActions = (state: RootState): Action[] => state.actions.actions;
export const selectActionTrackers = (state: RootState): ActionTracker[] => state.actions.trackers;
export const selectRunningRtuStates = (state: RootState): Record<string, RtuActionStateUpdateDto> =>
  state.actions.runningRtuStates;
export const selectActionNotifications = (state: RootState): ActionNotificationState[] =>
  state.actions.actionNotifications;
export const selectJustLaunchedActionId = (state: RootState): number | null =>
  state.actions.justLaunchedActionId;

export const selectUnreadActionNotifications = (state: RootState): ActionNotificationState[] =>
  state.actions.actionNotifications.filter((item) => !item.read);

export const selectRunningActionRtuIds = (state: RootState): number[] => {
  const ids = Object.values(state.actions.runningRtuStates)
    .filter((item) => item.running && item.rtuId !== undefined && item.rtuId !== null)
    .map((item) => item.rtuId as number);
  return Array.from(new Set(ids));
};

export const selectActiveTrackers = createSelector(
  [(state: RootState) => state.actions.trackers, (state: RootState) => state.actions.actions],
  (trackers, actions): Array<{ tracker: ActionTracker; action: Action }> =>
    trackers
      .map((tracker) => ({
        tracker,
        action: actions.find((item) => item.id === tracker.actionId),
      }))
      .filter((item): item is { tracker: ActionTracker; action: Action } => Boolean(item.action))
      .sort((a, b) => {
        const aTime = a.tracker.lastUpdatedAtUtc ?? a.tracker.createdAtUtc;
        const bTime = b.tracker.lastUpdatedAtUtc ?? b.tracker.createdAtUtc;
        return aTime < bTime ? 1 : -1;
      })
);

export const fetchActionsByIps = (ips: string[]): AppThunk => (dispatch, getState) => {
  const actions = selectActions(getState());
  const action = actions.find((item) => item.actionDetails.some((detail) => ips.includes(detail.ip)));
  if (action) {
    dispatch(addAction(action));
  }
};

export const selectActionById = (state: RootState, actionId: number): Action | undefined =>
  state.actions.actions.find((item) => item.id === actionId);

export const selectActionStatusSummary = (
  state: RootState,
  actionId: number
): { total: number; completed: number; failed: number; running: number; cancelled: number } => {
  const actionItem = state.actions.actions.find((item) => item.id === actionId);
  if (!actionItem) {
    return { total: 0, completed: 0, failed: 0, running: 0, cancelled: 0 };
  }

  const summary = summarizeActionProgress(actionItem);
  return {
    total: summary.total,
    completed: summary.completed,
    failed: summary.failed,
    running: summary.running,
    cancelled: summary.cancelled,
  };
};

export default actionsSlice.reducer;
